import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { WarehouseOption } from '../endpoints/integration/getIntegrationWarehouses';
import { acquireOperationContext, operationRequirement } from '@/session/operation';
import { useEndpoints } from '@/session/useEndpoints';
import { getSessionSubject } from '@/session/token';
import { CACHE_TTLS } from '@/session/cacheKeys';
import { peekIntegrationWarehouses, readIntegrationWarehouses, writeIntegrationWarehouses } from '@/session/tenantCache';
import { useOperationRuns } from '@/session/useOperationRuns';
import { utils } from '@/utils';

interface UseIntegrationWarehouseListProps {
  subscriptionId?: string;
  subscription?: { notify_by_type?: string | null };
  forceLoad?: boolean;
}

export const useIntegrationWarehouseList = ({ subscriptionId, subscription, forceLoad = false }: UseIntegrationWarehouseListProps) => {
  const sessionEndpoints = useEndpoints();
  const operationRuns = useOperationRuns();
  const organization = sessionEndpoints.organization;
  const subject = getSessionSubject();
  const subscriptionNotifyByType = subscription?.notify_by_type ?? null;
  const shouldLoadWarehouses = Boolean(subscriptionId && (forceLoad || subscriptionNotifyByType));
  const cacheIdentity = useMemo(
    () => (subject && subscriptionId ? { version: 2 as const, subject, organization, subscriptionId: String(subscriptionId) } : null),
    [organization, subject, subscriptionId]
  );
  const cached = cacheIdentity ? peekIntegrationWarehouses(cacheIdentity) : null;
  const [freshState, setFreshState] = useState<{
    subscriptionId: string | null;
    warehouses: WarehouseOption[];
    loading: boolean;
    error: string | null;
    // True only once a successful fetch (or a within-TTL cache entry written by one) has
    // confirmed the list. Callers that treat "value missing from the list" as an error must
    // gate on this: `warehouses` can be served from an arbitrarily old cache while a refresh
    // is in flight, or after a refresh failed, and neither is authoritative.
    resolved: boolean;
  }>({ subscriptionId: subscriptionId ?? null, warehouses: [], loading: false, error: null, resolved: false });
  const currentFreshState =
    freshState.subscriptionId === (subscriptionId ?? null)
      ? freshState
      : { subscriptionId: subscriptionId ?? null, warehouses: [] as WarehouseOption[], loading: false, error: null, resolved: false };

  useEffect(() => {
    if (!shouldLoadWarehouses || !subscriptionId || !cacheIdentity) return;
    const cachedValue = readIntegrationWarehouses(cacheIdentity);
    const isFresh = cachedValue !== null && Date.now() - cachedValue.refreshedAt <= CACHE_TTLS.integrationWarehouses;
    if (isFresh) {
      // A within-TTL entry was written by a successful fetch, so it is authoritative as-is.
      // Deferred rather than set synchronously so this cannot cascade a render before paint;
      // the guard makes it a no-op once the state already reflects the resolved cache.
      let active = true;
      void Promise.resolve().then(() => {
        if (!active) return;
        setFreshState((state) =>
          state.subscriptionId === subscriptionId && state.resolved && !state.loading && state.error === null
            ? state
            : { subscriptionId, warehouses: cachedValue.warehouses, loading: false, error: null, resolved: true }
        );
      });
      return () => {
        active = false;
      };
    }

    const operation = acquireOperationContext(organization);
    const run = operationRuns.begin(`warehouses:${subscriptionId}`, operation);
    run.publish(() =>
      setFreshState({
        subscriptionId,
        warehouses: cachedValue?.warehouses ?? [],
        loading: true,
        error: null,
        resolved: false,
      })
    );

    void sessionEndpoints.integrationData
      .getIntegrationWarehouses({ subscriptionId }, { operationRequirement: operationRequirement(operation, 'scope') })
      .then((response) => {
        if (response.isError) {
          if (utils.error.isCanceledResponse(response)) return;
          run.publish(() => {
            const error = 'Failed to fetch warehouses';
            toast.error(error);
            setFreshState({ subscriptionId, warehouses: cachedValue?.warehouses ?? [], loading: false, error, resolved: false });
          });
          return;
        }
        const warehouses = Array.isArray(response.data) ? response.data : [];
        run.publish(() => {
          writeIntegrationWarehouses(cacheIdentity, warehouses);
          setFreshState({ subscriptionId, warehouses, loading: false, error: null, resolved: true });
        });
      })
      .finally(() => {
        run.settle(() => setFreshState((state) => (state.subscriptionId === subscriptionId ? { ...state, loading: false } : state)));
      });

    return () => run.invalidate();
  }, [cacheIdentity, operationRuns, organization, sessionEndpoints.integrationData, shouldLoadWarehouses, subscriptionId]);

  const resolvedWarehouses = cached?.warehouses ?? currentFreshState.warehouses;

  return {
    warehouses: shouldLoadWarehouses ? resolvedWarehouses : [],
    loading: shouldLoadWarehouses ? currentFreshState.loading : false,
    error: shouldLoadWarehouses ? currentFreshState.error : null,
    resolved: shouldLoadWarehouses ? currentFreshState.resolved : false,
  };
};
